import { useMemo, useState } from 'react';
import { TextField } from '@scripsteam/scrips-signal-ds';
import { cn } from '@/lib/utils';
import { useCreateProviderFee } from '@/billing/services/use-provider-fees';
import { ServiceTypeahead } from './service-typeahead';
import { DoctorShareDialog } from './doctor-share-dialog';
import {
  blankDraft,
  buildPayload,
  draftFromService,
  formatSharePreview,
  type ProviderFeeDraft,
} from './provider-fee-draft';
import type { BillingServiceModel } from '@/shared/types/billing';
import type { OrgLocation } from '@/auth/types';

/**
 * Top-of-page CREATE banner. Mirrors Flutter `provider_fees_list_screen.dart`:
 * the inline form sits in its own band ABOVE the table toolbar / search /
 * archived link / data table — not inside the table. Its labels and widths
 * are independent of the table's column widths (same loose alignment as
 * Flutter / the PM source `provider-fee-add-row.tsx`).
 *
 * Recomposed on Signal DS: PM's lucide `Search`/`RotateCcw`/`Check`/`X` →
 * DS `TextField` + the shared `ServiceTypeahead` (DS `Icon name="search"`) +
 * composed glyph buttons (DS registry lacks reset/check/x glyphs — see
 * report DS-GAP). No hardcoded hex.
 */

interface ProviderFeeAddRowProps {
  providerId: string;
  locations: OrgLocation[];
}

export function ProviderFeeAddRow({ providerId, locations }: ProviderFeeAddRowProps) {
  // LOOP-SAFETY: destructure the stable `mutateAsync` — never depend on the
  // whole mutation object (its identity changes every render).
  const create = useCreateProviderFee();
  const { mutateAsync: createFee } = create;
  const submitting = create.isPending;

  const [draft, setDraft] = useState<ProviderFeeDraft>(blankDraft);
  const [shareOpen, setShareOpen] = useState(false);

  const hasAnyFee = useMemo(
    () => Object.values(draft.fees).some((f) => f.fee.trim() && parseFloat(f.fee) > 0),
    [draft.fees],
  );
  const canSave = Boolean(draft.serviceId) && hasAnyFee;
  const shareCount = Object.keys(draft.shares).length;

  const reset = () => setDraft(blankDraft());

  const onSubmit = async () => {
    if (!canSave) return;
    try {
      await createFee(buildPayload(draft, locations, providerId, null));
      reset();
    } catch {
      /* Global toast surfaces the failure. */
    }
  };

  const onPickService = (svc: BillingServiceModel) => {
    setDraft((prev) => draftFromService(prev, svc, locations));
  };

  const clearService = () => {
    setDraft((p) => ({
      ...p,
      serviceId: undefined,
      procedureCode: undefined,
      serviceQuery: '',
      fees: {},
    }));
  };

  return (
    <div className="px-8 py-4 bg-surface-card border-b border-border-default">
      {/* Field labels */}
      <div className="flex items-end gap-3">
        <FeeRowLabel className="flex-1 min-w-50" required>
          Service name
        </FeeRowLabel>
        <FeeRowLabel className="w-20 text-right">WTAX, %</FeeRowLabel>
        {locations.map((loc) => (
          <FeeRowLabel key={`lbl-${loc.id}`} className="w-24 text-right">
            {loc.name ?? ''}, AED
          </FeeRowLabel>
        ))}
        <span className="w-44" />
        <span className="w-16" />
      </div>

      {/* Inputs */}
      <div className="flex items-start gap-3 mt-2">
        <div className="flex-1 min-w-50">
          <ServiceTypeahead
            value={draft.serviceQuery}
            onTypingChange={(v) => setDraft((p) => ({ ...p, serviceQuery: v }))}
            onSelect={onPickService}
            onClear={clearService}
          />
        </div>

        <TextField
          type="text"
          size="compact"
          inputMode="decimal"
          placeholder="0.00"
          value={draft.wTax}
          onChange={(e) => setDraft((p) => ({ ...p, wTax: e.target.value }))}
          containerClassName="w-20"
          inputClassName="text-right tabular-nums"
          hideSpinButtons
          aria-label="WTAX percent"
        />

        {locations.map((loc) => {
          if (!loc.id) return null;
          const row = draft.fees[loc.id];
          const svcFee = row?.serviceFee;
          const canReset = svcFee != null;
          const share = draft.shares[loc.id];
          const preview = formatSharePreview(share);
          return (
            <div key={loc.id} className="flex flex-col items-stretch w-24 self-start">
              <div className="relative">
                <TextField
                  type="text"
                  size="compact"
                  inputMode="decimal"
                  placeholder="0.00"
                  value={row?.fee ?? ''}
                  onChange={(e) =>
                    setDraft((p) => ({
                      ...p,
                      fees: {
                        ...p.fees,
                        [loc.id!]: { ...(p.fees[loc.id!] ?? { fee: '' }), fee: e.target.value },
                      },
                    }))
                  }
                  containerClassName="w-full"
                  inputClassName="pr-8 text-right tabular-nums"
                  hideSpinButtons
                  aria-label={`${loc.name ?? 'Location'} fee`}
                />
                <button
                  type="button"
                  title="Reset to service default"
                  aria-label={`Reset ${loc.name ?? 'location'} fee to service default`}
                  disabled={!canReset}
                  onClick={() =>
                    setDraft((p) => ({
                      ...p,
                      fees: {
                        ...p.fees,
                        [loc.id!]: {
                          ...(p.fees[loc.id!] ?? { fee: '' }),
                          fee: svcFee != null ? String(svcFee) : '',
                        },
                      },
                    }))
                  }
                  className="absolute right-1 top-1/2 -translate-y-1/2 px-1 rounded text-xs text-text-hint hover:text-brand-primary disabled:opacity-30 disabled:cursor-not-allowed"
                >
                  ↺
                </button>
              </div>
              {preview && (
                <span className="mt-1 text-xs text-text-muted tabular-nums whitespace-nowrap">
                  {preview}
                </span>
              )}
            </div>
          );
        })}

        <button
          type="button"
          disabled={!hasAnyFee}
          onClick={() => setShareOpen(true)}
          className={cn(
            'h-10 w-44 px-3 rounded-md text-sm font-semibold transition-colors whitespace-nowrap',
            hasAnyFee
              ? 'bg-brand-primary text-white hover:bg-interactive-hover'
              : 'bg-grey-100 text-text-hint cursor-not-allowed',
          )}
        >
          {shareCount > 0 ? `Doctor Share (${shareCount})` : 'Add Doctor Share'}
        </button>

        <div className="flex items-center gap-1 w-16 justify-end">
          <button
            type="button"
            onClick={onSubmit}
            disabled={!canSave || submitting}
            title="Save"
            aria-label="Save"
            className="px-2 py-1 rounded text-sm font-bold text-status-success hover:bg-status-success-bg disabled:text-text-hint disabled:cursor-not-allowed"
          >
            ✓
          </button>
          <button
            type="button"
            onClick={reset}
            disabled={submitting}
            title="Clear"
            aria-label="Clear"
            className="px-2 py-1 rounded text-sm font-bold text-status-error hover:bg-status-error-bg"
          >
            ✕
          </button>
        </div>
      </div>

      <DoctorShareDialog
        open={shareOpen}
        onOpenChange={setShareOpen}
        locations={locations}
        providerFees={draft.fees}
        initialShares={draft.shares}
        onApply={(shares) => {
          setDraft((p) => ({ ...p, shares }));
          setShareOpen(false);
        }}
      />
    </div>
  );
}

/* NOT the design system's FieldLabel. This one is `font-semibold` + `tracking-wider`
 * where the graduated recipe is `font-bold` + `tracking-wide`, so adopting the DS
 * component here would silently restyle it. Renamed on 2026-09-20 only to clear the
 * import collision; the duplication is deliberate and VISIBLE.
 *
 * RULING OWED: is this a second, legitimate label style, or drift from the first? If it
 * is drift these call sites join `FieldLabel`; if it is real the design system owes a
 * variant. Either way it is a design decision, not a migration one. */
function FeeRowLabel({
  children,
  className,
  required,
}: {
  children: React.ReactNode;
  className?: string;
  required?: boolean;
}) {
  return (
    <span
      className={cn(
        'text-xs font-medium text-text-muted whitespace-nowrap',
        className,
      )}
    >
      {children}
      {required && <span className="text-status-error ml-1">*</span>}
    </span>
  );
}
